/**
 * Stock checks shared by the direct web cart pages (b2b-cart and cart). A cart line on its own
 * over its sellable stock is flagged on the line (stockShortItems, rowWarningOptions); lines that
 * each fit but together exceed the stock are reported in the error banner when Checkout is
 * clicked (validateCartStock), so no line reads "Insufficient stocks" while its quantity fits.
 *
 * The host component provides cart (with items), advancedUomStockQuantity and pushError(); it
 * overrides getStockInventories() when it keeps the warehouse inventories under another name.
 */
export default {
    computed: {
        /**
         * Cart lines short of stock (cart item id → error message), shared by the table's Error
         * Message column, its red rows and the list view. Each line is judged on its own
         * quantity, against the sellable stock it can draw on: when only the lines together
         * exceed it, checkout reports it instead (cart_checkout_product_backorder_forbidden), so
         * no line reads "Insufficient stocks" while its quantity fits its stock.
         */
        stockShortItems() {
            if (!window.bo.hasInventory) {
                return {};
            }

            const isShort = (product, productId, quantity) =>
                (parseFloat(quantity) || 0) > this.getLineSellableStock(product, productId);
            const shortItems = {};

            Object.values(this.cart.items).forEach((item) => {
                if (item._type != "bundle_cart_item") {
                    if (
                        isShort(item.product, item.product_id, item.quantity) &&
                        !this.getProductAllowBackorder(item.product)
                    ) {
                        shortItems[item.id] = window.bo.cartTranslations.insufficient_stocks;
                    }
                    return;
                }

                const disallow_children_backorders = item.product.disallow_children_backorders;
                let bundleShort =
                    isShort(item.product, item.product.id, item.quantity) &&
                    !this.getProductAllowBackorder(item.product);

                Object.values(item.composite_children).forEach((child) => {
                    const variation_allow_backorders =
                        child.product?.variations?.[child.product_id]?.allow_backorders ?? false;
                    if (
                        isShort(child.product, child.product_id, child.quantity) &&
                        (!variation_allow_backorders || disallow_children_backorders)
                    ) {
                        shortItems[child.id] = window.bo.cartTranslations.insufficient_stocks;
                        bundleShort = true;
                    }
                });

                if (bundleShort) {
                    shortItems[item.id] = window.bo.cartTranslations.bundle_insufficient_stocks;
                }
            });

            return shortItems;
        },
        // For showing warning color to the row
        rowWarningOptions() {
            const computedRowWarningOptions = {};
            Object.values(this.cart.items).forEach((item) => {
                if (item.id in this.stockShortItems) {
                    computedRowWarningOptions[item.id] = {
                        colorClass: "no-stock",
                    };
                }
            });

            return computedRowWarningOptions;
        },
    },

    methods: {
        // Warehouse inventories keyed by product id, each UOM's sellable stock as balance
        getStockInventories() {
            return this.warehouseInventories;
        },
        /**
         * Reports, in the error banner, each product whose cart lines together ask for more than
         * its sellable stock. A line on its own is flagged in the cart (stockShortItems); lines that
         * each fit but together do not only surface here, when Checkout is clicked. Mirrors
         * CheckoutCartValidator, which the server only reaches once every other check passes.
         */
        validateCartStock() {
            if (!window.bo.hasInventory) {
                return;
            }

            const pools = {};
            const addLine = (product, productId, quantity, allowBackorder) => {
                const variation = product?.variations?.[productId];
                // every UOM of a conv_rate group sells from one stock, counted in the base unit
                const key = variation
                    ? `${variation.parent_id ?? product.id}-${variation.conv_rate_group}`
                    : `bundle-${productId}`;
                const rate = variation ? this.getConversionRate(variation) : 1;

                if (!pools[key]) {
                    pools[key] = { requested: 0, stock: this.getBaseSellableStock(product, productId), line: null };
                }
                pools[key].requested += (parseFloat(quantity) || 0) * rate;
                // the error names the first line that cannot be backordered, in its own unit
                if (!allowBackorder && pools[key].line == null) {
                    pools[key].line = { sku: variation?.sku ?? product.sku, rate: rate, uom: variation?.uom?.name };
                }
            };

            Object.values(this.cart.items).forEach((item) => {
                if (item._type != "bundle_cart_item") {
                    addLine(item.product, item.product_id, item.quantity, this.getProductAllowBackorder(item.product));
                    return;
                }

                addLine(item.product, item.product.id, item.quantity, this.getProductAllowBackorder(item.product));
                Object.values(item.composite_children).forEach((child) => {
                    const variationAllowBackorders = child.product?.variations?.[child.product_id]?.allow_backorders ?? false;
                    // checkout submits a component's quantity per bundle times the bundle quantity
                    addLine(
                        child.product,
                        child.product_id,
                        (parseFloat(child.quantity) || 0) * (parseFloat(item.quantity) || 0),
                        variationAllowBackorders && !item.product.disallow_children_backorders
                    );
                });
            });

            Object.values(pools).forEach(({ requested, stock, line }) => {
                if (line != null && parseFloat(requested.toFixed(6)) > stock) {
                    this.pushError(
                        `cart_checkout_product_backorder_forbidden_${line.sku}`,
                        this.getStockShortMessage(line.sku, requested / line.rate, stock / line.rate, line.uom)
                    );
                }
            });
        },
        /**
         * Shared with the same error from the server, so both read the same. The stock is quoted
         * only to users who may see stock quantities; everyone else gets their cart total alone,
         * as does a server error that left the stock out.
         */
        getStockShortMessage(sku, quantity, sellable, uom) {
            const format = (value) => `${(parseFloat(value) || 0).toFixed(2)}${uom ? ` ${uom}` : ""}`;
            const translations = window.bo.cartTranslations;

            if (sellable == null || !this.canReadStockQuantities()) {
                return translations.cart_checkout_product_backorder_forbidden_without_stock
                    .replace(":sku", sku)
                    .replace(":quantity", format(quantity));
            }

            return translations.cart_checkout_product_backorder_forbidden
                .replace(":sku", sku)
                .replace(":quantity", format(quantity))
                .replace(":sellable", format(sellable));
        },
        // read_physical_stocks or read_sellable_stocks; b2b-cart and cart name these flags differently
        canReadStockQuantities() {
            const bo = window.bo;

            return !!(
                bo.hasCapabilityPhysicalStocks ||
                bo.hasCapabilitySellableStocks ||
                bo.hasCapabilityPhysical ||
                bo.hasCapabilitySellable
            );
        },
        getConversionRate(variation) {
            return Math.max(1, parseFloat(variation?.uom?.conversion_rate) || 1);
        },
        /**
         * Sellable stock a cart line can draw on, in its own unit. With advanced UOM stock quantity on
         * warehouseInventories holds only the line's own UOM's stock, while it may also sell from the
         * other UOMs of its conv_rate group, as add to cart and sales orders allow.
         */
        getLineSellableStock(product, productId) {
            const variation = product?.variations?.[productId];
            if (!this.advancedUomStockQuantity || !variation) {
                return this.getStockInventories()[productId]?.balance ?? 0;
            }

            return parseFloat(
                (this.getBaseSellableStock(product, productId) / this.getConversionRate(variation)).toFixed(6)
            );
        },
        /**
         * Sellable stock of the product a cart line draws on, in its base unit. warehouseInventories
         * holds each UOM's share of the base variation's stock, or each UOM's own stock with
         * advanced UOM stock quantity on (the whole conv_rate group's can be sold).
         */
        getBaseSellableStock(product, productId) {
            const variation = product?.variations?.[productId];
            if (!variation) {
                return this.getStockInventories()[productId]?.balance ?? 0;
            }

            const variations = Object.values(product.variations);
            if (this.advancedUomStockQuantity) {
                return variations
                    .filter((sibling) => sibling.conv_rate_group == variation.conv_rate_group)
                    .reduce(
                        (stock, sibling) =>
                            stock +
                            (this.getStockInventories()[sibling.id]?.balance ?? 0) * this.getConversionRate(sibling),
                        0
                    );
            }

            const baseVariation = variations.find(
                (sibling) =>
                    this.getConversionRate(sibling) == 1 && sibling.conv_rate_group == variation.conv_rate_group
            );
            if (baseVariation && this.getStockInventories()[baseVariation.id] != null) {
                return this.getStockInventories()[baseVariation.id].balance;
            }

            return (this.getStockInventories()[productId]?.balance ?? 0) * this.getConversionRate(variation);
        },
        getProductAllowBackorder(product) {
            if (!window.bo.hasWarehouse) return false;
            if (product.type == "variable") {
                if (product.selected_variation_id) {
                    return product.getSelectedVariation().allow_backorders;
                } else if (product.default_variation) {
                    return product.default_variation.allow_backorders;
                } else if (product.base_variation) {
                    return product.base_variation.allow_backorders;
                }
            } else if (product.type == "bundle" || product.type == "variation") {
                return product.allow_backorders;
            }
            return false;
        },
    },
};
